import type { ValueType } from "@csbi/metric-catalog"

export type ValueFormat = "auto" | "compact" | "full" | "percent" | "duration"

const COMPACT_THRESHOLD = 10_000

const group = (n: number, digits = 0): string =>
  n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })

function compact(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return group(n)
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return "—"
  const s = Math.round(Math.abs(seconds))
  const sign = seconds < 0 ? "-" : ""

  if (s < 60) return `${sign}${s}s`
  if (s < 3600) {
    const m = Math.floor(s / 60)
    const rest = s % 60
    return rest ? `${sign}${m}m ${rest}s` : `${sign}${m}m`
  }
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m ? `${sign}${h}h ${m}m` : `${sign}${h}h`
}

export type FormatOptions = {
  valueType?: ValueType
  format?: ValueFormat
  context?: "axis" | "label" | "table"
}

export function formatValue(
  value: number | string | null | undefined,
  { valueType = "count", format = "auto", context = "label" }: FormatOptions = {},
): string {
  if (value == null || value === "") return "—"
  if (typeof value === "string") {
    if (valueType === "timestamp") return formatTimestamp(value)
    return value
  }
  if (!Number.isFinite(value)) return "—"

  if (format === "percent" || valueType === "ratio")
    return `${(value * 100).toFixed(value >= 0.1 || value === 0 ? 1 : 2)}%`

  if (format === "duration" || valueType === "duration_sec") return formatDuration(value)

  if (format === "compact") return compact(value)
  if (format === "full") return group(value)

  // auto: compact on axes and for large numbers, full otherwise — tables are always full, because there the reader is comparing digits
  if (context === "table") return group(value)
  if (context === "axis" || Math.abs(value) >= COMPACT_THRESHOLD) return compact(value)
  return group(value)
}

export function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`
}

/**
 * ⚠ **No timezone conversion at all — display the date exactly as the string spells it.**
 */
export function formatPeriod(iso: string, granularity: "day" | "week" | "month" | "all"): string {
  if (granularity === "all") return "Current"

  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (parts) {
    const [, year, month, day] = parts
    return granularity === "month" ? `${year}-${month}` : `${month}/${day}`
  }

  // Anything not of that shape (which should not occur) falls back to Date parsing — at least do not dump the raw string onto the axis
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const s = d.toISOString()
  return granularity === "month" ? s.slice(0, 7) : s.slice(5, 10).replace("-", "/")
}

export function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v)
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s
}
